import React, { useEffect, useState } from "react";
import { getEventsPlotData } from "../../utils/Requests/Analysis";
import {
    isSolarPhotovoltaicByCaller,
    isSolarThermalByCaller,
    isThermalByCaller,
    isWaterByCaller
} from "../../utils/Types";
import { AlertTriangle, ThumbsUp } from "react-feather";
import { Badge, Space } from "antd";
import { AiOutlineAlert, AiOutlineSignature } from "react-icons/ai";
import Skeleton from "react-loading-skeleton";
import _ from "lodash";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { globalStatusOptions } from "../Forms/Inputs/EventsInput";

const getPlantsBadgeStatusStorageKey = (props) =>
    `seye.plantsBadgeStatus.${props.caller}`;

const savePlantsBadgeStatus = (props, status) => {
    if (!status) return;

    sessionStorage.setItem(
        getPlantsBadgeStatusStorageKey(props),
        status
    );
};

const getSavedPlantsBadgeStatus = (props) => {
    return sessionStorage.getItem(
        getPlantsBadgeStatusStorageKey(props)
    );
};

const clearPlantsBadgeStatus = (props) => {
    sessionStorage.removeItem(
        getPlantsBadgeStatusStorageKey(props)
    );
};

const getPlantsTableStorageKey = (props) =>
    `seye.plantsTable.${props.caller}`;

const getPlantsStatusStorageKey = (props) =>
    `seye.plantsStatus.${props.caller}`;

const getPlantsFilterClearedKey = (props) =>
    `seye.plantsFilterCleared.${props.caller}`;

export const getStatusSelected = (props, location) => {
    const statusFromLocation = _.get(location, 'state.status', null);

    /*
     * Si venimos directamente de un clic sobre un badge usamos location.state.
     *
     * Si hemos entrado en un SEYE y volvemos atrás, location.state puede
     * no contener status. En ese caso recuperamos el badge guardado.
     */
    const status =
        statusFromLocation ||
        getSavedPlantsBadgeStatus(props);

    const selectedStatus = _.find(
        globalStatusOptions,
        { id: status }
    );

    const badgeSelectedId = _.get(
        selectedStatus,
        'id',
        -1
    );

    const badgeSelectedStyle = _.get(
        selectedStatus,
        'selectedStyle',
        {}
    );

    let statusNameField = 'plant_status_id';

    if (badgeSelectedId === 'offer_pending_approval') {
        statusNameField = 'plant_manual_status';
    }

    return {
        ...selectedStatus,
        badgeSelectedId,
        badgeSelectedStyle,
        field: statusNameField,
        value: _.get(selectedStatus, 'value', null)
    };
};

const successRequest = (
    configuration,
    setConfiguration,
    response
) => {
    const data = _.get(response, "status", {});

    const summary = {
        offer_pending_approval: _.get(
            data,
            "offer_pending_approval",
            0
        ),
        ok: _.get(data, "ok", 0),
        warning: _.get(data, "warning", 0),
        fail: _.get(data, "no_reading", 0)
    };

    setConfiguration({
        ...configuration,
        text: configuration.text,
        url: configuration.url,
        isLoading: false,
        isError: false,
        data: summary
    });
};

const failRequest = (
    configuration,
    setConfiguration,
    response
) => {
    setConfiguration({
        ...configuration,
        text: configuration.text,
        url: configuration.url,
        isLoading: false,
        isError: true,
        data: {}
    });
};

const RouteLink = (props) => {
    const option = _.find(globalStatusOptions, {
        id: props.type
    });

    const id = _.get(option, "id", -1);

    const url = (() => {
        if (isSolarThermalByCaller(props)) {
            return "/dashboard/plants/thermal";
        }

        if (isSolarPhotovoltaicByCaller(props)) {
            return "/dashboard/plants/photovoltaic";
        }

        if (isWaterByCaller(props)) {
            return "/dashboard/plants/water";
        }

        if (isThermalByCaller(props)) {
            return "/dashboard/plants/temperature";
        }

        return "/dashboard/plants";
    })();

    return (
        <Link
            to={url}
            state={{ status: id }}
            onClick={(event) => {
                event.preventDefault();

                props.handlerOnClick(
                    url,
                    { status: id },
                    props
                );
            }}
        >
            {props.children}
        </Link>
    );
};

const BadgeLink = ({ ...props }) => {
    const wrapperIconStyle = {
        display: "flex",
        flexDirection: "row",
        alignContent: "center",
        justifyContent: "center",
        alignItems: "center",
        width: "40px",
        height: "40px",
        borderRadius: "0.358rem",
        ...props.badgeSelectedStyle
    };

    return (
        <RouteLink
            handlerOnClick={props.handlerOnClick}
            {...props}
            type={props.type}
        >
            <Badge
                className={props.className}
                count={props.count}
                overflowCount={100}
                color={props.color}
            >
                <div
                    className={props.className}
                    style={wrapperIconStyle}
                >
                    {props.icon}
                </div>
            </Badge>
        </RouteLink>
    );
};

export function BadgeGlobalStatusSummary({ ...props }) {
    const location = useLocation();
    const navigate = useNavigate();

    const {
        badgeSelectedId,
        badgeSelectedStyle
    } = getStatusSelected(props, location);

    const [configuration, setConfiguration] = useState({
        ...props,
        text: "time.today",
        url: "time.today",
        timeLine: {
            text: "time.today"
        },
        isLoading: true,
        isError: false,
        data: {},
        clickCount: {
            fail: 0,
            ok: 0,
            warning: 0
        }
    });

    useEffect(() => {
        let isMounted = true;

        if (!configuration.isLoading) {
            return undefined;
        }

        const setData = () => {
            getEventsPlotData(
                {
                    ...configuration,
                    plantId: configuration.plantId,
                    interval: configuration.url
                },
                (response) => {
                    if (!isMounted) {
                        return null;
                    }

                    successRequest(
                        configuration,
                        setConfiguration,
                        response
                    );

                    return null;
                },
                (response) => {
                    if (!isMounted) {
                        return null;
                    }

                    failRequest(
                        configuration,
                        setConfiguration,
                        response
                    );

                    return null;
                }
            );
        };

        setData();

        return () => {
            isMounted = false;
        };
    }, [configuration]);

    

    const clearSavedFilter = () => {
        sessionStorage.removeItem(
            getPlantsTableStorageKey(props)
        );

        sessionStorage.removeItem(
            getPlantsStatusStorageKey(props)
        );

        sessionStorage.setItem(
            getPlantsFilterClearedKey(props),
            "1"
        );
    };

    const enableSavedFilter = () => {
        sessionStorage.removeItem(
            getPlantsFilterClearedKey(props)
        );
    };

    const handlerOnClick = (url, state, badgeProps) => {
    const typeStatusLink = _.get(
        badgeProps,
        'type',
        -1
    );

    /*
     * Segundo clic sobre el mismo badge:
     * quitamos completamente el filtro.
     */
    if (typeStatusLink === badgeSelectedId) {
        clearPlantsBadgeStatus(props);

        sessionStorage.removeItem(
            `seye.plantsTable.${props.caller}`
        );

        sessionStorage.removeItem(
            `seye.plantsStatus.${props.caller}`
        );

        sessionStorage.setItem(
            `seye.plantsFilterCleared.${props.caller}`,
            '1'
        );

        navigate(url, {
            replace: false,
            state: {
                clearPlantsFilter: true
            }
        });

        return;
    }

    /*
     * Nuevo filtro seleccionado.
     */
    savePlantsBadgeStatus(
        props,
        typeStatusLink
    );

    sessionStorage.removeItem(
        `seye.plantsFilterCleared.${props.caller}`
    );

    navigate(url, {
        replace: false,
        state
    });
};

    return (
        <>
            {configuration.isLoading &&
                !configuration.isError && (
                    <Skeleton
                        style={{
                            width: "150px"
                        }}
                    />
                )}

            {configuration.isError && (
                <span className="mt-1 ms-1">
                    Error...
                </span>
            )}

            {!configuration.isLoading &&
                !configuration.isError && (
                    <Space size="middle">
                        <BadgeLink
                            {...props}
                            handlerOnClick={handlerOnClick}
                            type="ok"
                            className="bg-light-success"
                            count={_.get(
                                configuration,
                                "data.ok",
                                0
                            )}
                            overflowCount={100}
                            color="#28c76f"
                            icon={
                                <ThumbsUp
                                    style={{
                                        width: "28px",
                                        height: "28px",
                                        strokeWidth: "2px"
                                    }}
                                />
                            }
                            badgeSelectedStyle={
                                badgeSelectedId === "ok"
                                    ? badgeSelectedStyle
                                    : {}
                            }
                        />

                        <BadgeLink
                            {...props}
                            handlerOnClick={handlerOnClick}
                            type="warning"
                            className="bg-light-warning"
                            count={_.get(
                                configuration,
                                "data.warning",
                                0
                            )}
                            overflowCount={100}
                            color="#ff9f43"
                            icon={
                                <AlertTriangle
                                    style={{
                                        width: "28px",
                                        height: "28px",
                                        strokeWidth: "2px"
                                    }}
                                />
                            }
                            badgeSelectedStyle={
                                badgeSelectedId === "warning"
                                    ? badgeSelectedStyle
                                    : {}
                            }
                        />

                        <BadgeLink
                            {...props}
                            handlerOnClick={handlerOnClick}
                            type="fail"
                            className="bg-light-danger"
                            count={_.get(
                                configuration,
                                "data.fail",
                                0
                            )}
                            overflowCount={100}
                            icon={
                                <AiOutlineAlert
                                    style={{
                                        width: "28px",
                                        height: "28px",
                                        strokeWidth: "2px"
                                    }}
                                />
                            }
                            badgeSelectedStyle={
                                badgeSelectedId === "fail"
                                    ? badgeSelectedStyle
                                    : {}
                            }
                        />

                        <BadgeLink
                            {...props}
                            handlerOnClick={handlerOnClick}
                            type="offer_pending_approval"
                            className="bg-approval-offer"
                            count={_.get(
                                configuration,
                                "data.offer_pending_approval",
                                0
                            )}
                            overflowCount={100}
                            color="rgb(129 129 129)"
                            icon={
                                <AiOutlineSignature
                                    style={{
                                        width: "28px",
                                        height: "28px",
                                        strokeWidth: "2px"
                                    }}
                                    color="rgb(129 129 129)"
                                />
                            }
                            badgeSelectedStyle={
                                badgeSelectedId ===
                                    "offer_pending_approval"
                                    ? badgeSelectedStyle
                                    : {}
                            }
                        />
                    </Space>
                )}
        </>
    );
}